import axios from 'axios';

export type ContentfulEntriesQuery = Record<string, string | number | boolean | undefined>;
type ContentfulCacheEntry = {
    savedAt: number;
    data: any;
};

type FetchCacheOptions = {
    ttlMs?: number;
    preferCache?: boolean;
};

const CACHE_PREFIX = 'amp.contentful';
const DEFAULT_CACHE_TTL_MS = 5 * 60 * 1000;

const normalizeApiBase = (): string => {
    const base = (process.env.PLATFORM_API_URL || '').replace(/\/$/, '');
    if (!base) {
        return '';
    }
    if (base.includes('/api/')) {
        return base;
    }
    const version = process.env.PLATFORM_API_VERSION || 'v1';
    return `${base}/api/${version}`;
};

const buildUrl = (path: string) => {
    const apiBase = normalizeApiBase();
    if (!apiBase) {
        return path;
    }
    return `${apiBase}${path.startsWith('/') ? path : `/${path}`}`;
};

const getStorage = () => {
    if (typeof window === 'undefined') {
        return null;
    }
    return window.localStorage;
};

const sortObject = (value: Record<string, unknown>) => {
    return Object.keys(value)
        .sort()
        .reduce(
            (acc, key) => {
                acc[key] = value[key];
                return acc;
            },
            {} as Record<string, unknown>,
        );
};

const getCacheKey = (scope: string, query: ContentfulEntriesQuery) => {
    const sortedQuery = sortObject(query as Record<string, unknown>);
    return `${CACHE_PREFIX}:${scope}:${JSON.stringify(sortedQuery)}`;
};

const readCache = (key: string, ttlMs: number) => {
    const storage = getStorage();
    if (!storage) {
        return null;
    }
    try {
        const raw = storage.getItem(key);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as ContentfulCacheEntry;
        if (!parsed?.savedAt || Date.now() - parsed.savedAt > ttlMs) {
            storage.removeItem(key);
            return null;
        }
        return parsed.data;
    } catch {
        return null;
    }
};

const writeCache = (key: string, data: any) => {
    const storage = getStorage();
    if (!storage) {
        return;
    }
    try {
        const payload: ContentfulCacheEntry = {
            savedAt: Date.now(),
            data,
        };
        storage.setItem(key, JSON.stringify(payload));
    } catch {
        // Ignore storage quota/serialization failures and continue without cache.
    }
};

export const fetchAuthenticatedContentfulEntries = async (query: ContentfulEntriesQuery, token: string) => {
    const response = await axios.get(buildUrl('/contentful/entries'), {
        params: query,
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    return response.data;
};

export const fetchAuthenticatedContentfulEntriesCached = async (
    query: ContentfulEntriesQuery,
    token: string,
    options: FetchCacheOptions = {},
) => {
    const ttlMs = options.ttlMs ?? DEFAULT_CACHE_TTL_MS;
    const preferCache = options.preferCache ?? true;
    const cacheKey = getCacheKey('auth', query);
    if (preferCache) {
        const cached = readCache(cacheKey, ttlMs);
        if (cached) {
            return cached;
        }
    }
    const data = await fetchAuthenticatedContentfulEntries(query, token);
    writeCache(cacheKey, data);
    return data;
};

export const fetchPublicContentfulEntries = async (query: ContentfulEntriesQuery) => {
    const response = await axios.get(buildUrl('/contentful/public/entries'), {
        params: query,
    });
    return response.data;
};

export const fetchPublicContentfulEntriesCached = async (
    query: ContentfulEntriesQuery,
    options: FetchCacheOptions = {},
) => {
    const ttlMs = options.ttlMs ?? DEFAULT_CACHE_TTL_MS;
    const preferCache = options.preferCache ?? true;
    const cacheKey = getCacheKey('public', query);
    if (preferCache) {
        const cached = readCache(cacheKey, ttlMs);
        if (cached) {
            return cached;
        }
    }
    const data = await fetchPublicContentfulEntries(query);
    writeCache(cacheKey, data);
    return data;
};
